import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { FormControl, FieldCheckbox, Icon } from 'components/atoms';
import Select from 'components/atoms/field-select';

import { GatewayPermissions } from 'auth/constants/permissions';
import { GatewayReportTypeNames } from 'auth/constants/reportsTypes';

import { ChildContainer, DownloadText, ErrorMessage, FixedWidthButton } from '../ThirdParty.styles';
import { useUserSettings, useTerminologyConfig } from 'context/DataContext';

const DownloadSummary = ({
  thirdpartySearchTerm,
  filters,
  sort,
  showAllThirdparties,
  onCancel,
  isLoading,
  requestError,
  isDownloadEnabled,
  onDownloadSumaryRequest,
  isLinkedThirdpartiesFeatureEnabled,
}) => {
  const { hasPermission } = useUserSettings();
  const terminologyConfig = useTerminologyConfig();
  const [isFiltersApplied, setIsFiltersApplied] = useState(false);

  const hasInitiateMSSorSSS: boolean =
    hasPermission(GatewayPermissions.InitiateDueDiligenceManagedServiceScreening) ||
    hasPermission(GatewayPermissions.InitiateDueDiligenceSelfServiceScreening);

  const hasOrderMDD: boolean = hasPermission(GatewayPermissions.ViewDueDiligenceReport);

  const downloadSummarySchema = yup.object().shape({
    reportType: yup.mixed().test('reportType', 'Please select a report type', (value) => {
      return value !== '';
    }),
  });
  const downloadSummaryForm = useForm({
    mode: 'all',
    resolver: yupResolver(downloadSummarySchema),
    defaultValues: {
      reportType: null,
      filteredView: false,
      includeChildren: false,
    },
  });

  const {
    handleSubmit: handleSubmitDownloadSummary,
    getValues: getValuesDownloadSummary,
    watch: watchDownloadSummary,
    register: registerDownloadSummary,
    formState: {
      errors: errorsDownloadSummary,
      touchedFields: touchedFieldsDownloadSummary,
      isValid: isValidReportType,
    },
  } = downloadSummaryForm;

  const handleConfirmationModalOpen = () => {
    const formData = getValuesDownloadSummary();
    const reportSelectedOption = reportTypeOptions[formData.reportType];

    if (reportSelectedOption) {
      let reportTypeEndpoint = '';
      switch (reportSelectedOption) {
        case GatewayReportTypeNames.ThirdPartySummaryReport:
          reportTypeEndpoint = 'onlinereport/thirdparty-summary-report';
          break;
        case GatewayReportTypeNames.ScreeningSummaryReport:
          reportTypeEndpoint = 'onlinereport/screening-summary-report';
          break;
        case GatewayReportTypeNames.DueDiligenceSummaryReport:
          reportTypeEndpoint = 'onlinereport/msdd-summary-report';
          break;
        default:
          onCancel();
          return;
      }

      const submissionData = {
        ...(formData.filteredView && thirdpartySearchTerm && { Query: thirdpartySearchTerm }),
        ...(formData.filteredView && { filter: filters }),
        includeChildren: formData.includeChildren,
        sort: sort,
        ShowAll: showAllThirdparties,
        reportTypeEndpoint,
      };

      onDownloadSumaryRequest(submissionData);
    } else {
      onCancel();
    }
  };

  const reportTypeOptions = useMemo(() => {
    const options = [
      GatewayReportTypeNames.ThirdPartySummaryReport,
      ...(hasInitiateMSSorSSS ? [GatewayReportTypeNames.ScreeningSummaryReport] : []),
      ...(hasOrderMDD ? [GatewayReportTypeNames.DueDiligenceSummaryReport] : []),
    ];
    return options.sort((a, b) => a.localeCompare(b));
  }, [hasInitiateMSSorSSS, hasOrderMDD]);

  const reportTypeWatch = watchDownloadSummary
    ? reportTypeOptions[watchDownloadSummary('reportType')]
    : null;

  const reportTypeOptionsMap = (options) => {
    return options.map((value, index) => ({
      value: index,
      name:
        value === GatewayReportTypeNames.ThirdPartySummaryReport
          ? `${terminologyConfig?.thirdPartySingularNaming} Summary Report`
          : value,
    }));
  };

  const mappedReportTypeOptions = reportTypeOptionsMap(reportTypeOptions);

  useEffect(() => {
    if (
      thirdpartySearchTerm ||
      filters?.riskRating?.length > 0 ||
      filters?.thirdPartyCountryCodes?.length > 0 ||
      filters?.thirdPartyDueDiligenceTypes?.length > 0 ||
      filters?.thirdPartyTypes?.length > 0
    ) {
      setIsFiltersApplied(true);
    } else {
      setIsFiltersApplied(false);
    }
  }, [
    thirdpartySearchTerm,
    filters?.riskRating,
    filters?.thirdPartyCountryCodes,
    filters?.thirdPartyDueDiligenceTypes,
    filters?.thirdPartyTypes,
  ]);

  return (
    <>
      <form onSubmit={handleSubmitDownloadSummary(handleConfirmationModalOpen)}>
        <FormControl errorMessage={!isValidReportType && errorsDownloadSummary.reportType?.message}>
          <Select
            label="Summary Type"
            id="reportType"
            register={registerDownloadSummary}
            placeholder={'Select a report type'}
            placeholderDisabled={true}
            options={mappedReportTypeOptions}
            valid={isValidReportType && touchedFieldsDownloadSummary?.reportType}
            fullWidth
            required
          />
        </FormControl>
        <label>Options </label>
        {!isLinkedThirdpartiesFeatureEnabled && (
          <FieldCheckbox
            id="includeChildren"
            register={registerDownloadSummary}
            label={`Include Linked ${terminologyConfig?.thirdPartyPluralNaming}`}
          />
        )}
        {isFiltersApplied && (
          <FieldCheckbox
            id="filteredView"
            register={registerDownloadSummary}
            defaultChecked={false}
            label="Filtered view only"
          />
        )}
        <ChildContainer>
          <FixedWidthButton
            name="DownloadReport"
            secondary
            type="submit"
            disabled={
              !isDownloadEnabled || isLoading || (!reportTypeWatch && reportTypeWatch !== null)
            }
          >
            <Icon icon="download" colour="white" size="xxs" aria-hidden />
            <DownloadText>Download Summary</DownloadText>
          </FixedWidthButton>
        </ChildContainer>
        {requestError && (
          <div>
            <ErrorMessage>
              Error generating the report. Please try again. If the problem persists, please contact
              vantagesupport@controlrisks.com
            </ErrorMessage>
          </div>
        )}
      </form>
    </>
  );
};

export default DownloadSummary;
